import Http from '../../network/Http'
import NewMeeting from './model/NewMeeting'
import {ContentType} from '../../network/Constants'
import {DetailsWithInformation} from '../../network/response/DetailsWithInformation'
import {ApiResponse} from '../../network/response/ApiResponse'
import MeetingSimple from './model/MeetingSimple'
import UpdateMeeting from './model/UpdateMeeting'
import {MeetingFile} from './model/MeetingFile'
import {MeetingForAdmin, MeetingParticipantAssocDirector} from './model/MeetingForAdmin'
import {ATTENDANCE_STATUS, AVAILABLE_OR_NOT, ROLE_FOR_ADMINISTRATOR, VALID_OR_NOT} from '../../CodeMaster'
import moment from 'moment'
import {Nullable} from '../../app/TypeHelpers'
import MeetingCommentDetail from './model/MeetingCommentDetail'
import DeletedCommentDetail from './model/DeletedCommentDetail'
import DeletedComment from './model/DeletedComment'
import {
    CommentReactedUserResponse,
    convertToCommentReactedUser,
    convertToMeetingComment,
    convertToMeetingCommentReply,
    convertToMeetingCommentView,
    MeetingAttendanceResponseResponse,
    MeetingCommentDetailResponse,
    MeetingCommentViewResponse,
    MeetingSimpleResponse,
} from './MeetingReposioryCommons'
import NewMeetingComment from './model/NewMeetingComment'
import {MeetingCommentFile} from './model/MeetingCommentFile'
import UpdateMeetingComment from './model/UpdateMeetingComment'
import {CommentReactedUser} from './model/MeetingComment'
import MeetingCommentView from './model/MeetingCommentView'
import DeletedCommentView from './model/DeletedCommentView'
import {FileDataForValidate} from './model/FileDataForValidate'
import {MeetingLink} from './model/MeetingLink'
import {CommentNotificationData} from './model/CommentNotificationData'
import NewMeetingAttendanceResponseForAdmin from './model/NewMeetingAttendanceResponseForAdmin'
import UpdateMeetingAttendanceResponseForAdmin from './model/UpdateMeetingAttendanceResponseForAdmin'
import MeetingAttendanceResponse from './model/MeetingAttendanceResponse'
import {Room} from './model/Room'
import {MeetingAttachmentResoponse} from './model/Meeting'
import {MailContent} from './model/MailContent'

interface MeetingParticipantAssocDirectorResponse extends Omit<MeetingParticipantAssocDirector, 'meetingAttendanceResponse'> {
    meetingAttendanceResponse: Nullable<MeetingAttendanceResponseResponse>
}

interface MeetingForAdminResponse extends Omit<MeetingForAdmin,
    'attendanceReplyDeadline' |
    'attendanceReplyRequestDatetime' |
    'manageAttendance' |
    'proxyAttendanceAvailability' |
    'participantAssocDirectorList' |
    'meetingAttachmentFileList' |
    'meetingAttachmentLinkList' |
    'meetingReportList'> {
    attendanceReplyDeadline: Nullable<string>,
    attendanceReplyRequestDatetime: Nullable<string>,
    manageAttendance: string,
    proxyAttendanceAvailability: Nullable<string>,
    participantAssocDirectorList: MeetingParticipantAssocDirectorResponse[]
    meetingAttachmentFileList: MeetingAttachmentResoponse[],
    meetingAttachmentLinkList: MeetingAttachmentResoponse[],
    meetingReportList: MeetingAttachmentResoponse[],
}

interface NotificationDataWithMeetingId {
    meetingId: number
    meetingVersion: number
    title: string
    body: string
    tag: string
    url: string
    targetDate: string
}

interface AttendanceReplyRequestData extends NotificationDataWithMeetingId {
    attendanceReplyDeadline: string
    proxyAttendanceAvailability: string
}

export class MeetingRepositoryForAdmin {
    http: Http

    constructor(http: Http) {
        this.http = http
    }

    getEndpoint(condominiumCd: string): string {
        return `/api/admin/condominiums/${condominiumCd}/meetings`
    }

    getCommentEndPoint(condominiumCd: string, meetingId: number): string {
        return this.getEndpoint(condominiumCd) + `/${meetingId}/comments`
    }

    getCommentReplyEndPoint(condominiumCd: string, meetingId: number, commentId: number): string {
        return this.getCommentEndPoint(condominiumCd, meetingId) + `/${commentId}/replies`
    }

    async getMeetings(condominiumCd: string): Promise<MeetingSimple[]> {
        const apiResponseJSON: ApiResponse<MeetingSimpleResponse[]> = await this.http.get(
            this.getEndpoint(condominiumCd),
        )
        const details = apiResponseJSON.details
        return details.map(detail => ({
            ...detail,
            attendanceReplyDeadline: detail.attendanceReplyDeadline === null ? null :
                moment(detail.attendanceReplyDeadline),
            proxyAttendanceAvailability: detail.proxyAttendanceAvailability === null ? null :
                AVAILABLE_OR_NOT.codeToEnumConstants(detail.proxyAttendanceAvailability),
        }))
    }

    async getMeeting(condominiumCd: string, meetingId: number): Promise<MeetingForAdmin> {
        const apiResponseJSON: ApiResponse<MeetingForAdminResponse> = await this.http.get(
            this.getEndpoint(condominiumCd) + `/${meetingId}`,
        )
        const details = apiResponseJSON.details
        return {
            ...details,
            attendanceReplyDeadline: details.attendanceReplyDeadline === null ? null :
                moment(details.attendanceReplyDeadline),
            attendanceReplyRequestDatetime: details.attendanceReplyRequestDatetime === null ? null :
                moment(details.attendanceReplyRequestDatetime),
            manageAttendance: VALID_OR_NOT.codeToEnumConstants(details.manageAttendance),
            proxyAttendanceAvailability: details.proxyAttendanceAvailability === null ? null :
                AVAILABLE_OR_NOT.codeToEnumConstants(details.proxyAttendanceAvailability),
            meetingAnnounceNotifyDatetime: details.meetingAnnounceNotifyDatetime === null ? null :
                moment(details.meetingAnnounceNotifyDatetime),
            participantAssocDirectorList: details.participantAssocDirectorList.map(participant => {
                const attendanceData = participant.meetingAttendanceResponse
                const roomList: Room[] = participant.roomList.map(room => {
                    return Room.from(room)
                })
                return {
                    ...participant,
                    roomList,
                    meetingAttendanceResponse: (attendanceData === null) ? null :
                        {
                            ...attendanceData,
                            attendanceStatus: ATTENDANCE_STATUS.codeToEnumConstants(attendanceData.attendanceStatus),
                            responseDateTime: moment(attendanceData.responseDateTime),
                        },
                }
            }),
            meetingAttachmentFileList: details.meetingAttachmentFileList.map(file => {
                const roomList: Room[] = file.roomList.map(room => {
                    return Room.from(room)
                })
                return {
                    ...file,
                    roleId: ROLE_FOR_ADMINISTRATOR.codeToEnumConstants(file.roleId),
                    roomList: roomList,
                }
            }),
            meetingAttachmentLinkList: details.meetingAttachmentLinkList.map(link => {
                const roomList: Room[] = link.roomList.map(room => {
                    return Room.from(room)
                })
                return {
                    ...link,
                    roleId: ROLE_FOR_ADMINISTRATOR.codeToEnumConstants(link.roleId),
                    roomList: roomList,
                }
            }),
            meetingReportList: details.meetingReportList.map(report => {
                const roomList: Room[] = report.roomList.map(room => {
                    return Room.from(room)
                })
                return {
                    ...report,
                    roleId: ROLE_FOR_ADMINISTRATOR.codeToEnumConstants(report.roleId),
                    roomList: roomList,
                }
            }),
        }
    }

    async getComments(
        condominiumCd: string, meetingId: number,
    ): Promise<(MeetingCommentView | DeletedCommentView)[]> {
        const apiResponseJSON: ApiResponse<MeetingCommentViewResponse[]> = await this.http.get(
            this.getEndpoint(condominiumCd) + `/${meetingId}/comments`,
        )

        const details = apiResponseJSON.details
        return details.map(comment => {
            return convertToMeetingCommentView(comment)
        })
    }

    async getCommentDetail(
        condominiumCd: string,
        meetingId: number,
        commentId: number,
    ): Promise<MeetingCommentDetail | DeletedCommentDetail> {
        const apiResponseJSON: ApiResponse<MeetingCommentDetailResponse> = await this.http.get(
            this.getCommentEndPoint(condominiumCd, meetingId) + `/${commentId}`,
        )

        const details = apiResponseJSON.details
        const commentReplyList = details.commentReplyList
            .map(reply => convertToMeetingCommentReply(reply, commentId))

        const converted = convertToMeetingComment(details)

        if (converted instanceof DeletedComment) {
            return new DeletedCommentDetail(
                converted.meetingCommentId,
                converted.message,
                commentReplyList,
            )
        } else {
            return new MeetingCommentDetail(
                converted.meetingCommentId,
                converted.content,
                converted.user,
                converted.updateDatetime,
                converted.commentReactedUserList,
                converted.attachmentList,
                converted.isYou,
                converted.version,
                commentReplyList,
            )
        }
    }

    async registerMeeting(condominiumCd: string, newMeeting: NewMeeting)
        : Promise<DetailsWithInformation<number>> {
        const apiResponseJSON: ApiResponse<number> = await this.http.post(
            this.getEndpoint(condominiumCd),
            newMeeting,
            ContentType.JSON,
        )

        const apiResponse: ApiResponse<number> =
            ApiResponse.from(
                apiResponseJSON,
                (details: any): number => details,
            )

        return new DetailsWithInformation<number>(
            apiResponse.details,
            apiResponse.informations[0],
        )
    }

    async updateMeeting(condominiumCd: string, meetingId: number, updateMeeting: UpdateMeeting)
        : Promise<DetailsWithInformation<number>> {
        const apiResponseJSON: ApiResponse<number> = await this.http.put(
            this.getEndpoint(condominiumCd) + `/${meetingId}`,
            updateMeeting,
        )

        const apiResponse: ApiResponse<number> =
            ApiResponse.from(
                apiResponseJSON,
                (details: any): number => details,
            )

        return new DetailsWithInformation<number>(
            apiResponse.details,
            apiResponse.informations[0],
        )
    }

    async deleteMeeting(condominiumCd: string, meetingId: number, version: number)
        : Promise<DetailsWithInformation<void>> {
        const apiResponseJSON: ApiResponse<void> = await this.http.delete(
            this.getEndpoint(condominiumCd) + `/${meetingId}?version=${version}`,
        )

        const apiResponse: ApiResponse<void> =
            ApiResponse.from(apiResponseJSON, () => undefined)

        return new DetailsWithInformation<void>(
            apiResponse.details,
            apiResponse.informations[0],
        )
    }

    async registerMeetingFile(
        condominiumCd: string,
        meetingId: number,
        meetingFile: MeetingFile,
    ): Promise<void> {
        await this.http.post(
            `/api/admin/condominiums/${condominiumCd}/meetings/${meetingId}/files`,
            meetingFile,
            ContentType.JSON,
        )
    }

    async registerMeetingReport(
        condominiumCd: string,
        meetingId: number,
        meetingReport: MeetingFile,
    ): Promise<void> {
        await this.http.post(
            `/api/admin/condominiums/${condominiumCd}/meetings/${meetingId}/reports`,
            meetingReport,
            ContentType.JSON,
        )
    }

    async registerComment(
        condominiumCd: string,
        meetingId: number,
        newMeetingComment: NewMeetingComment,
    ): Promise<number> {
        const apiResponseJSON: ApiResponse<number> = await this.http.post(
            this.getCommentEndPoint(condominiumCd, meetingId),
            newMeetingComment,
            ContentType.JSON,
        )

        return apiResponseJSON.details
    }

    async registerCommentReply(
        condominiumCd: string,
        meetingId: number,
        commentId: number,
        newMeetingComment: NewMeetingComment,
    ): Promise<number> {
        const apiResponseJSON: ApiResponse<number> = await this.http.post(
            this.getCommentReplyEndPoint(condominiumCd, meetingId, commentId),
            newMeetingComment,
            ContentType.JSON,
        )

        return apiResponseJSON.details
    }

    async updateComment(
        condominiumCd: string,
        meetingId: number,
        commentId: number,
        updateMeetingComment: UpdateMeetingComment,
    ): Promise<void> {
        await this.http.put(
            this.getCommentEndPoint(condominiumCd, meetingId) + `/${commentId}`,
            updateMeetingComment,
        )
    }

    async updateCommentReply(
        condominiumCd: string,
        meetingId: number,
        commentId: number,
        commentReplyId: number,
        updateMeetingComment: UpdateMeetingComment,
    ): Promise<void> {
        await this.http.put(
            this.getCommentReplyEndPoint(condominiumCd, meetingId, commentId) + `/${commentReplyId}`,
            updateMeetingComment,
        )
    }

    async deleteComment(
        condominiumCd: string,
        meetingId: number,
        commentId: number,
    ): Promise<void> {
        await this.http.delete(
            this.getCommentEndPoint(condominiumCd, meetingId) + `/${commentId}`,
        )
    }

    async deleteCommentReply(
        condominiumCd: string,
        meetingId: number,
        commentId: number,
        commentReplyId: number,
    ): Promise<void> {
        await this.http.delete(
            this.getCommentReplyEndPoint(condominiumCd, meetingId, commentId) + `/${commentReplyId}`,
        )
    }

    async registerCommentFile(
        condominiumCd: string,
        meetingId: number,
        commentId: number,
        commentFile: MeetingCommentFile,
    ): Promise<void> {
        await this.http.post(
            this.getCommentEndPoint(condominiumCd, meetingId) + `/${commentId}/files`,
            commentFile,
            ContentType.JSON,
        )
    }

    async registerCommentReplyFile(
        condominiumCd: string,
        meetingId: number,
        commentId: number,
        commentReplyId: number,
        commentReplyFile: MeetingCommentFile,
    ): Promise<void> {
        await this.http.post(
            this.getCommentReplyEndPoint(condominiumCd, meetingId, commentId) + `/${commentReplyId}/files`,
            commentReplyFile,
            ContentType.JSON,
        )
    }

    async updateCommentReaction(
        condominiumCd: string,
        meetingId: number,
        commentId: number,
        isReaction: boolean,
    ): Promise<CommentReactedUser[]> {
        const apiResponseJSON: ApiResponse<CommentReactedUserResponse[]> = await this.http.put(
            this.getCommentEndPoint(condominiumCd, meetingId) + `/${commentId}/reaction`,
            {isReaction: isReaction},
        )

        const details = apiResponseJSON.details
        return details.map(commentReactedUser => convertToCommentReactedUser(commentReactedUser))
    }

    async updateCommentReplyReaction(
        condominiumCd: string,
        meetingId: number,
        commentId: number,
        commentReplyId: number,
        isReaction: boolean,
    ): Promise<CommentReactedUser[]> {
        const apiResponseJSON: ApiResponse<CommentReactedUserResponse[]> = await this.http.put(
            this.getCommentReplyEndPoint(condominiumCd, meetingId, commentId) + `/${commentReplyId}/reaction`,
            {isReaction: isReaction},
        )

        const details = apiResponseJSON.details
        return details.map(commentReactedUser => convertToCommentReactedUser(commentReactedUser))
    }

    async getMeetingAnnounceContent(
        condominiumCd: string,
        meetingId: number,
        meetingVersion: number
    ): Promise<MailContent> {
        const apiResponseJSON: ApiResponse<MailContent> = await this.http.get(
            this.getEndpoint(condominiumCd) + `/${meetingId}/notifications/meeting-announce/mail-content?meetingVersion=${meetingVersion}`,
        )

        return apiResponseJSON.details
    }

    async sendMeetingAnnounce(
        condominiumCd: string,
        notifyTitle: string,
        notifyBody: string,
        meetingId: number,
        meetingDate: string,
        meetingVersion: number,
    ): Promise<void> {
        const notificationData: NotificationDataWithMeetingId = {
            meetingId: meetingId,
            meetingVersion: meetingVersion,
            title: notifyTitle,
            body: notifyBody,
            tag: `AssocMeetingAnnounce-${meetingId}`,
            url: `${document.location.origin}/resident/login?seniurl=/resident/condominiums/${condominiumCd}/meetings/${meetingId}`,
            targetDate: meetingDate,
        }

        await this.http.post(
            `/api/admin/condominiums/${condominiumCd}/meetings/${meetingId}/notifications/meeting-announce`,
            notificationData,
            ContentType.JSON,
        )
    }

    async getMeetingAttendanceReplyRequestContent(
        condominiumCd: string,
        meetingId: number,
        attendanceReplyDeadline: string,
        meetingVersion: number
    ): Promise<MailContent> {
        const apiResponseJSON: ApiResponse<MailContent> = await this.http.get(
            this.getEndpoint(condominiumCd) + `/${meetingId}/notifications/meeting-attendance-reply-request/mail-content?attendanceReplyDeadline=${attendanceReplyDeadline}&meetingVersion=${meetingVersion}`,
        )

        return apiResponseJSON.details
    }

    async sendMeetingAttendanceReplyRequest(
        condominiumCd: string,
        notifyTitle: string,
        notifyBody: string,
        meetingId: number,
        meetingDate: string,
        attendanceReplyDeadline: string,
        proxyAttendanceAvailability: string,
        meetingVersion: number,
    ): Promise<void> {
        const attendanceReplyRequestData: AttendanceReplyRequestData = {
            meetingId: meetingId,
            meetingVersion: meetingVersion,
            title: notifyTitle,
            body: notifyBody,
            tag: `AssocMeetingAttendanceReplyRequest-${meetingId}`,
            url: `${document.location.origin}/resident/login?seniurl=/resident/condominiums/${condominiumCd}/meetings/${meetingId}`,
            targetDate: meetingDate,
            attendanceReplyDeadline: attendanceReplyDeadline,
            proxyAttendanceAvailability: proxyAttendanceAvailability,
        }

        await this.http.post(
            `/api/admin/condominiums/${condominiumCd}/meetings/${meetingId}/notifications/meeting-attendance-reply-request`,
            attendanceReplyRequestData,
            ContentType.JSON,
        )
    }

    async validateParallelFileUpload(
        fileDataForValidate: FileDataForValidate,
    ): Promise<void> {
        await this.http.post(
            '/api/common/validate/files/parallel-file-upload',
            fileDataForValidate,
            ContentType.JSON,
        )
    }

    async validateMeetingLink(meetingLink: MeetingLink): Promise<ApiResponse<void>> {
        const response = await this.http.post(
            '/api/admin/validate/meeting/link',
            meetingLink,
            ContentType.JSON,
        )

        return ApiResponse.from(response, t => t)
    }

    async sendCommentNotification(
        condominiumCd: string,
        commentNotificationData: CommentNotificationData,
    ): Promise<void> {
        await this.http.post(
            `/api/admin/condominiums/${condominiumCd}/managers/notifications/mail-comment-notifications`,
            commentNotificationData,
            ContentType.JSON,
        )
    }

    async registerAttendance(
        condominiumCd: string,
        meetingId: number,
        newMeetingAttendance: NewMeetingAttendanceResponseForAdmin,
    ): Promise<MeetingAttendanceResponse> {
        const apiResponseJSON: ApiResponse<MeetingAttendanceResponseResponse> = await this.http.post(
            this.getEndpoint(condominiumCd) + `/${meetingId}/attendance`,
            {
                ...newMeetingAttendance,
                attendanceStatus: newMeetingAttendance.attendanceStatus.value,
            },
            ContentType.JSON,
        )

        const details = apiResponseJSON.details
        return new MeetingAttendanceResponse(
            ATTENDANCE_STATUS.codeToEnumConstants(details.attendanceStatus),
            details.attendanceId,
            details.note,
            details.proxyUserName,
            details.proxyUserRelationship,
            moment(details.responseDateTime),
            details.agentRespondentAdminId,
            details.agentRespondentRoleName,
            details.agentRespondentName,
            details.version
        )
    }

    async updateAttendance(
        condominiumCd: string,
        meetingId: number,
        attendanceId: number,
        updateMeetingAttendance: UpdateMeetingAttendanceResponseForAdmin,
    ): Promise<MeetingAttendanceResponse> {
        const apiResponseJSON: ApiResponse<MeetingAttendanceResponseResponse> = await this.http.put(
            this.getEndpoint(condominiumCd) + `/${meetingId}/attendance/${attendanceId}`,
            {
                ...updateMeetingAttendance,
                attendanceStatus: updateMeetingAttendance.attendanceStatus.value,
            },
        )
        const details = apiResponseJSON.details
        return new MeetingAttendanceResponse(
            ATTENDANCE_STATUS.codeToEnumConstants(details.attendanceStatus),
            details.attendanceId,
            details.note,
            details.proxyUserName,
            details.proxyUserRelationship,
            moment(details.responseDateTime),
            details.agentRespondentAdminId,
            details.agentRespondentRoleName,
            details.agentRespondentName,
            details.version
        )
    }
}
